import React, { useEffect, useRef, useState } from "react";
import {
  Form,
  Row,
  Col,
  InputGroup,
  Card,
  CardBody,
  FormGroup,
  Table,
  Button,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { API_ENDPOINTS } from "../../constant/_constant";
import { toast } from "react-toastify";
import usePostRequest from "../../hooks/usePostRequest";
import moment from "moment";
import DynamicPagination from "../../components/DynamicPagination ";
import {
  IconBox,
  IconCalendarWeek,
  IconCopy,
  IconCopyCheck,
  IconEdit,
  IconEye,
  IconFileSearch,
  IconFileTypePdf,
  IconFileTypeXls,
  IconFilter,
  IconHistoryToggle,
  IconLayoutGridFilled,
  IconLayoutListFilled,
  IconMailForward,
  IconMailSpark,
  IconMessageCircle,
  IconPaperclip,
  IconSearch,
  IconTrash,
  IconUpload,
} from "@tabler/icons-react";
import View_DetailsProduct from "./ViewDetail/View_DetailsProduct";
import useIdleLogout from "../../hooks/useIdleLogout";
import UpdateProductsPopup from "./UpdateProductsPopup";
import ComanConfirmationModal from "../../components/ComanConfirmationModal";
import SkuListUI from "./SkuListUI";
import UploadStepPopup from "./BatchImport/UploadStepPopup";
import axiosInstance from "../../api/axiosInstance";
import Filters from "./FiltersModal/Filters";
import ReviewProductsPopup from "./BatchImport/ReviewProductsPopup";
import ProductChangeHistoryModal from "./ChangeHistory/ProductChangeHistoryModal";

const FILTER_STORAGE_KEY = "skuListing.filters";

const loadPersisted = () => {
  try {
    return JSON.parse(localStorage.getItem(FILTER_STORAGE_KEY)) || {};
  } catch {
    return {};
  }
};

function SkuListing() {
  const _persisted = loadPersisted();
  const navigate = useNavigate();
  const [page, setPage] = useState(_persisted.page ?? 1);
  const [limit, setLimit] = useState(_persisted.limit ?? 50);
  const [isClicked, setIsClicked] = useState(false);
  const [isListView, setIsListView] = useState(false);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isImportSummaryOpen, setIsImportSummaryOpen] = useState(false);
  const [importSummary, setImportSummary] = useState(null);
  /* Product whose change history popup is open (null = closed). */
  const [historyProduct, setHistoryProduct] = useState(null);
  const [isViewOpen, setIsViewOpen] = useState({
    show: false,
    order: [],
    isStatusUpdate: false,
  });

  const [isUpdateProd, setIsUpdateProd] = useState({
    show: false,
    prodId: "",
    skuId: "",
  });
  const [isOpen, setIsOpen] = useState(false);
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const filterBtnRef = useRef(null);

  const [isModalOpen, setIsModalOpen] = useState({
    show: false,
    action: null,
    message: "",
  });

  const { data: vendorlist, postData: fetchVendorList } = usePostRequest(
    API_ENDPOINTS.GET_VENDORS,
  );

  const { data: vendors, postData: fetchVendors } = usePostRequest(
    API_ENDPOINTS.GET_SKU_LISTING,
  );

  const [sku, setSku] = useState(
    _persisted.sku ?? { vendor: "", skuId: "", type: "" }
  );

  useEffect(() => {
    localStorage.setItem(
      FILTER_STORAGE_KEY,
      JSON.stringify({ sku, page, limit })
    );
  }, [sku, page, limit]);

  const [globalTotal, setGlobalTotal] = useState(0);

  const handlePageChange = (nextPage) => {
    setPage(nextPage);
  };

  const handleLimitChange = (selectedLimit) => {
    setLimit(selectedLimit);
  };

  useEffect(() => {
    fetchVendorList({ page: 1, limit: 500, is_relevent: true });
  }, []);

  useEffect(() => {
    const requestParams = {
      page,
      limit,
      vendor_id: sku.vendor || null,
      search: sku.skuId || null,
      type: sku.type || null,
    };

    fetchVendors(requestParams);
    setIsClicked(false);
  }, [
    page,
    limit,
    isClicked,
    // sku.vendor,
    // sku.skuId
  ]);

  const getPaginationTotal = (pagination) =>
    pagination?.totalProducts ??
    pagination?.totalSkus ??
    pagination?.totalRecords ??
    pagination?.totalItems ??
    pagination?.total ??
    0;

  const filteredTotal = getPaginationTotal(vendors?.pagination);

  useEffect(() => {
    if (!sku.vendor && !sku.skuId && !sku.type) {
      setGlobalTotal(getPaginationTotal(vendors?.pagination));
    }
  }, [vendors]);

  const refetchPreserve = () => {
    fetchVendors({
      page,
      limit,
      vendor_id: sku.vendor || null,
      search: sku.skuId || null,
      type: sku.type || null,
    });
  };

  const handleClearFilter = () => {
    setSku({ vendor: "", skuId: "", type: "" });
    setPage(1);
    localStorage.removeItem(FILTER_STORAGE_KEY);
    setIsClicked(true);
  };

  const handleSearch = () => {
    setPage(1);
    setIsClicked((prev) => !prev);
  };

  const handleFiltersApply = (values) => {
    setSku((prevSku) => ({
      ...prevSku,
      vendor: values.vendor || "",
      type: values.type || "",
    }));
    setPage(1);
    setIsClicked((prev) => !prev);
  };

  const handleViewClick = (vendor) => {
    setIsViewOpen({
      show: true,
      products: vendor,
    });

    const currentUrl = window.location.pathname;
    const newUrl = `${currentUrl}?${vendor.id}`;
    window.history.pushState(null, "", newUrl);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSku((prevSku) => ({
      ...prevSku,
      [name]: value,
    }));
  };

  const truncateString = (str, num) => {
    if (str.length > num) {
      return str.slice(0, num) + "...";
    } else {
      return str;
    }
  };

  useIdleLogout();

  const handleUpdateClick = (sku_id) => {
    navigate(`/update-product/${sku_id}`);
  };

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = `${process.env.PUBLIC_URL}/template.xlsx`;
    link.download = "template.xlsx";
    link.click();
  };

  const handleDownloadExcel = async () => {
    try {
      const response = await axiosInstance.post(
        API_ENDPOINTS.PRODUCT_EXCEL_DOWNLOAD,
        {
          vendor_id: sku.vendor || undefined,
          search: sku.skuId || undefined,
          type: sku.type || undefined,
        },
        {
          responseType: "blob",
        },
      );

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `products_${moment().format("YYYY-MM-DD")}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Excel download failed:", error);
      toast.error("Excel download failed. Please try again.");
    }
  };

  // const totalNotes = vendors?.section.reduce((acc, section) => {
  //       return acc + section.notes.length;
  //   }, 0);

  const openModalCopy = (product) => {
    let modalMessage = `Are you sure you want to duplicate this "${product?.sku_id}" product?`;
    let modalTitle = "Duplicate Order";
    let type = "success";
    let modalIcon = <IconCopy stroke={1} />;
    const productId = product?.sku_id;
    let actionHandler = () => {
      // CopyOrderHandler(order);
      navigate("/create-product", { state: { productId } });
    };
    let action = "Change";
    setIsModalOpen({
      show: true,
      action,
      message: modalMessage,
      onConfirm: actionHandler,
      title: modalTitle,
      icon: modalIcon,
      type: type,
    });
  };

  return (
    <>
      <UploadStepPopup
        show={isUploadOpen}
        setShow={setIsUploadOpen}
        onNext={(data) => {
          setImportSummary(data);
          setIsImportSummaryOpen(true);
        }}
      />
      <ReviewProductsPopup
        show={isImportSummaryOpen}
        setShow={setIsImportSummaryOpen}
        summaryData={importSummary}
        // onApprove={(data) => {
        //   handleClearFilter();
        // }}
        onApprove={(data) => {
          refetchPreserve();
        }}
      />
      <ComanConfirmationModal
        show={isModalOpen.show}
        onHide={() => {
          setIsModalOpen({ ...isModalOpen, show: false });
          setIsOpen(false);
        }}
        title={isModalOpen.title}
        message={isModalOpen.message}
        onConfirm={isModalOpen.onConfirm}
        icon={isModalOpen.icon}
        type={isModalOpen.type}
      />
      {isUpdateProd.show && (
        <UpdateProductsPopup
          show={isUpdateProd.show}
          skuId={isUpdateProd.skuId}
          productId={isUpdateProd.prodId}
          setShow={setIsUpdateProd}
          // onDeleteSuccess={handleClearFilter}
          onDeleteSuccess={refetchPreserve}
        />
      )}
      <View_DetailsProduct
        isStatusUpdate={isViewOpen.isStatusUpdate}
        onStatusUpdate={() =>
          setIsViewOpen((prev) => ({ ...prev, isStatusUpdate: true }))
        }
      />
      {/* <div className="page-header">
        <h2 className="page-title">Product Listing</h2>
      </div> */}
      {isListView ? null : (
        <>
          <div className="page-header">
            <div className="row g-2 align-items-center">
              <div className="col">
                <h2 className="page-title">Products</h2>
              </div>
              <div className="col d-none d-sm-inline">
                <Button
                  className="btn-warning float-end"
                  onClick={() => setIsUploadOpen(true)}
                >
                  <IconUpload size={20} /> Batch Import
                </Button>
              </div>
            </div>
          </div>

          {/* <Card className="mb-2 shadow-sm">
            <Card.Body className="p-3">
              <Form
                className="d-flex align-items-center gap-4 position-relative"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleSearch();
                  }
                }}
              >
                <InputGroup>
                  <Form.Control
                    className="bg-light shadow-none"
                    placeholder="Search anything..."
                    name="skuId"
                    value={sku.skuId}
                    onChange={handleInputChange}
                    autoComplete="off"
                  />
                  <InputGroup.Text className="bg-white border-start-0">
                    <IconSearch stroke={2} />
                  </InputGroup.Text>
                </InputGroup>

                <div className="d-flex align-items-center gap-2 border_filter position-relative">
                  <Button
                    ref={filterBtnRef}
                    className="filter-btn"
                    onClick={() => setShowFiltersModal((p) => !p)}
                  >
                    <IconFilter size={16} />
                    <span>Filters</span>
                    {(sku.vendor || sku.type) && (
                      <span
                        className="ms-1 badge rounded-pill text-white"
                        style={{
                          backgroundColor: "#fe6338",
                          fontSize: 11,
                          padding: "2px 6px",
                        }}
                      >
                        {[sku.vendor, sku.type].filter(Boolean).length}
                      </span>
                    )}
                  </Button>

                  <Filters
                    show={showFiltersModal}
                    onHide={() => setShowFiltersModal(false)}
                    anchorRef={filterBtnRef}
                    initialValues={{ vendor: sku.vendor, type: sku.type }}
                    vendorlist={vendorlist?.data || []}
                    matchCount={filteredTotal}
                    totalCount={globalTotal}
                    onApply={handleFiltersApply}
                  />
                </div>

                <div className="d-flex align-items-center gap-2 border_filter">
                  <Button variant="warning" onClick={handleSearch}>
                    <IconSearch size={16} /> Search
                  </Button>
                  <Button
                    variant="outline-success"
                    onClick={handleDownloadExcel}
                    className="d-flex align-items-center gap-1"
                  >
                    <IconFileTypeXls size={16} /> Download
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card> */}
          {/* New toolbar layout */}

          <Card className="mb-3">
            <CardBody>
              <Form
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleSearch();
                  }
                }}
              >
                <Row>
                  <Col md={3}>
                    <FormGroup>
                      <Form.Label className="">Search</Form.Label>
                      <InputGroup className="mb-3">
                        <Form.Control
                          name="skuId"
                          value={sku.skuId}
                          onChange={handleInputChange}
                          placeholder="Search...."
                          aria-label="sku-id"
                          aria-describedby="basic-addon1"
                        />
                      </InputGroup>
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup>
                      <Form.Label className="">Select Vendor</Form.Label>
                      <Form.Select
                        name="vendor"
                        value={sku.vendor}
                        onChange={handleInputChange}
                        aria-label="Select Vendor"
                      >
                        <option value="">All</option>
                        {vendorlist?.data?.map((vendor) => (
                          <option
                            key={vendor.VendorContact?.vendor_id}
                            value={vendor.VendorContact?.vendor_id ?? 0}
                          >
                            {vendor.display_name}
                          </option>
                        ))}
                      </Form.Select>
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup>
                      <Form.Label className="">Product Type</Form.Label>
                      <Form.Select
                        name="type"
                        value={sku.type}
                        onChange={handleInputChange}
                        aria-label="Select Type"
                      >
                        <option value="">All</option>
                        <option value="inventory">Inventory</option>
                        <option value="non-inventory">Non-Inventory</option>
                        <option value="kit">Kit</option>
                        {/* {vendorlist?.data?.map((vendor) => (
                    <option
                      key={vendor.VendorContact?.vendor_id}
                      value={vendor.VendorContact?.vendor_id ?? 0}
                    >
                      {vendor.display_name}
                    </option>
                  ))} */}
                      </Form.Select>
                    </FormGroup>
                  </Col>
                  <Col md={4} className="mb-2">
                    <Form.Label className="form-label">&nbsp;</Form.Label>
                    <div className="d-flex gap-2">
                      <Button
                        className="w-100"
                        variant="warning"
                        onClick={handleSearch}
                      >
                        Apply Filters
                      </Button>
                      <Button
                        className="w-100"
                        variant="outline-secondary"
                        onClick={handleClearFilter}
                      >
                        Clear Filters
                      </Button>
                      <Button
                        className="w-100 d-flex align-items-center justify-content-center gap-1"
                        variant="outline-success"
                        onClick={handleDownloadExcel}
                      >
                        <IconFileTypeXls size={16} /> Download
                      </Button>
                    </div>
                  </Col>
                  <Form.Text>
                    Search by SKU, name, description, vendor name, product type,
                    or any other relevant keyword.
                  </Form.Text>
                </Row>
              </Form>
            </CardBody>
          </Card>
        </>
      )}
      {isListView ? null : (
        <div className="d-flex align-items-center justify-content-end pt-2 pb-3 gap-3">
          <div className="view-toggle d-flex align-items-center justify-content-end gap-1">
            <IconLayoutGridFilled
              onClick={() => setIsListView(false)}
              style={{
                cursor: "pointer",
                color: !isListView ? "#FE6338" : "#515151",
              }}
            />
            <IconLayoutListFilled
              onClick={() => setIsListView(true)}
              style={{
                cursor: "pointer",
                color: isListView ? "#FE6338" : "#515151",
              }}
            />
          </div>
        </div>
      )}
      <style>
        {`
      .product-card-equal-row {
        align-items: stretch;
      }

      .product-card-equal-row > .col-md-3 {
        display: flex;
      }

      .product-card-equal {
        width: 100%;
        max-height: calc(100% - 1.5rem);
        display: flex;
        flex-direction: column;
      }

      .product_equalcard-body {
        flex: 1;
        display: flex;
        flex-direction: column;
      }

      .product_equalcard-body .ftr_card_box {
        margin-top: auto;
      }
    `}
      </style>
      {isListView ? (
        <SkuListUI />
      ) : (
        <>
          <Row className="product-card-equal-row">
            {vendors?.data?.map((vendor, index) => (
              <Col md={3} key={index} className="d-flex">
                <Card
                  className="product-card-equal status_item_card w-100 me-0 mb-4"
                  onClick={() => handleViewClick(vendor)}
                  style={{ cursor: "pointer" }}
                >
                  <Card.Body className="product_equalcard-body">
                    <div className="rfq_card_row">
                      <div className="rfq_item_card">
                        <OverlayTrigger
                          placement="bottom"
                          overlay={
                            <Tooltip id="tooltip-product-name">
                              {vendor?.name || "No Product Name"}
                            </Tooltip>
                          }
                        >
                          <div className="value_card d-flex align-items-center">
                            {/* Product Name:{" "} */}
                            <p className="label_card text-orange">
                              {vendor?.name || "N/A"}
                            </p>
                          </div>
                        </OverlayTrigger>
                      </div>


                    </div>




                    <div className="rfq_card_row">
                      <div className="rfq_item_card">
                        <div className="value_card">Vendor SKU</div>
                        <p className="label_card text-orange">
                          #{vendor?.sku_display_name || "NA"}
                        </p>
                      </div>


                      <div className="rfq_item_card">
                        <div className="value_card">Internal SKU</div>
                        <p className="label_card">
                          {/* {truncateString(
                        vendor?.Vendor?.display_name || "N/A",
                        100,
                      )} */}
                          {vendor?.vendor_sku || "N/A"}
                        </p>
                      </div>


                    </div>

                    <div className="rfq_card_row">
                      <div className="rfq_item_card">
                        <div className="value_card">Vendor Name</div>
                        <p className="label_card">
                          {/* {truncateString(
                        vendor?.Vendor?.display_name || "N/A",
                        100,
                      )} */}
                          {vendor?.Vendor?.display_name || "N/A"}
                        </p>
                      </div>
                      <div className="rfq_item_card">
                        <div className="value_card">Product Type</div>
                        <p className="label_card">
                          {vendor?.type
                            ? vendor.type.charAt(0).toUpperCase() +
                            vendor.type.slice(1)
                            : "N/A"}
                        </p>
                      </div>
                      {/* <div className="rfq_item_card">
                    <div className="value_card">Created Date</div>
                    <p className="label_card">
                      {moment(vendor?.createdAt).format("MM/DD/YYYY")}
                    </p>
                  </div> */}
                    </div>


                    {/* <div className="rfq_card_row">
                      <div className="rfq_item_card">
                        <div className="value_card">Product Description</div>
                        <p className="label_card">
                          {truncateString(
                            vendor?.description || "No Description",
                            50,
                          )}
                        </p>
                      </div>
                    </div> */}

                    <div className="button-groups">
                      <div className="badge_btn">
                        <span
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewClick(vendor);
                          }}
                        >
                          <IconEye size={24} />
                        </span>
                        <b> View Details </b>
                      </div>
                      <div className="badge_btn">
                        <span
                          onClick={(e) => {
                            e.stopPropagation();
                            openModalCopy(vendor);
                          }}
                        >
                          <IconCopyCheck />
                        </span>
                        <b> Duplicate </b>
                      </div>
                      <div className="badge_btn">
                        <span
                          onClick={(e) => {
                            e.stopPropagation();
                            setHistoryProduct(vendor);
                          }}
                        >
                          <IconHistoryToggle size={24} />
                        </span>
                        <b> Changes </b>
                      </div>
                      <div className="badge_btn">
                        <span
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsUpdateProd({
                              show: true,
                              prodId: vendor?.id,
                              skuId: vendor?.sku_id,
                            });
                          }}
                        >
                          <IconTrash size={24} />
                        </span>
                        <b> Delete </b>
                      </div>
                      {/* <div className="badge_btn">
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload();
                      }}
                    >
                      <IconFileTypeXls size={24} />
                    </span>
                    <b> Download Templates </b>
                  </div> */}
                    </div>

                    <div className="rfq_card_row mt-3">
                      <div className="rfq_item_card total_amt_rfq">
                        <strong>Cost:</strong>
                        <strong className="amt_bold">
                          {" "}
                          $ {vendor?.aspetto_price || "0.00"}
                        </strong>
                      </div>
                      <div className="rfq_item_card total_amt_rfq">
                        <strong>Price:</strong>
                        <strong className="amt_bold">
                          {" "}
                          $ {vendor?.customer_price || "0.00"}
                        </strong>
                      </div>
                    </div>

                    <div className="ftr_card_box">
                      <a
                        className="big_badge bg-cyan-lt"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleUpdateClick(vendor.id);
                        }}
                      >
                        <IconEdit width={18} height={18} /> Edit
                      </a>
                      <div className="rfq_lis_short">
                        {vendor.type === "kit" && (
                          <span>
                            <IconBox stroke={1} />
                            {vendor.ProductKits.length}
                          </span>
                        )}
                        <span>
                          <IconPaperclip stroke={1} />
                          {vendor.Attachments?.length ?? 0}
                        </span>
                        <span>
                          <IconMessageCircle stroke={1} />
                          {vendor.section.reduce((acc, section) => {
                            return acc + section.notes.length;
                          }, 0)}
                        </span>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
          {vendors?.data?.length > 0 ? (
            <DynamicPagination
              totalPages={vendors?.pagination?.totalPages}
              currentPage={page}
              onPageChange={handlePageChange}
              limitPerPage={limit}
              onLimitChange={handleLimitChange}
              limitOptions={[50, 75, 100, 200]}
            />
          ) : (
            <p className="text-center mt-2">No matching records found.</p>
          )}
        </>
      )}

      {historyProduct && (
        <ProductChangeHistoryModal
          show={Boolean(historyProduct)}
          onHide={() => setHistoryProduct(null)}
          productId={historyProduct.id}
          skuId={historyProduct.sku_id}
          productType={historyProduct.type}
        />
      )}
    </>
  );
}

export default SkuListing;
